import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  Input,
  Row,
  Col,
} from "reactstrap";
import { getUserName, getUserId, postMethod } from "services/httpServices";

const DRAFT_KEY = "add_stock_draft";

const saveDraftToStorage = (data) => {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
};

const getDraftFromStorage = () => {
  const data = localStorage.getItem(DRAFT_KEY);
  return data ? JSON.parse(data) : null;
};

const clearDraftFromStorage = () => {
  localStorage.removeItem(DRAFT_KEY);
};

const AddStocks = ({ closeModal }) => {
  const [stocks, setStocks] = useState([
    {
      productId: "",
      quantity: "",
      source: "",
      referenceNote: "",
    },
  ]);
  const [products, setProducts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await postMethod(
          "/api/Inventory/GetAllProducts",
          {},
          "post"
        );
        if (res?.status === 200) {
          setProducts(res.data.result || []);
        }
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };

    fetchProducts();

    const draft = getDraftFromStorage();
    if (draft) {
      setStocks(draft);
    }
  }, []);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...stocks];
    updated[index][name] = value;
    setStocks(updated);
  };

  const handleAdd = () => {
    setStocks([
      ...stocks,
      {
        productId: "",
        quantity: "",
        source: "",
        referenceNote: "",
      },
    ]);
  };

  const handleRemove = (index) => {
    const copy = [...stocks];
    copy.splice(index, 1);
    setStocks(copy);
  };

  const validate = () => {
    const errs = stocks.map((s) => {
      const missing = [];
      if (!s.productId) missing.push("Product");
      if (!s.quantity) missing.push("Quantity");
      if (!s.source) missing.push("Source");
      return missing.length > 0
        ? `Missing fields: ${missing.join(", ")}`
        : null;
    });
    setErrors(errs);
    return errs.every((e) => e === null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const now = new Date().toISOString();

    const payload = stocks.map((s) => ({
      id: 0,
      merchantId: 0,
      merchantUserId: getUserId(),
      productId: parseInt(s.productId),
      quantity: parseInt(s.quantity),
      source: s.source,
      referenceNote: s.referenceNote,
      createdAt: now,
      createdBy: getUserName(),
      updatedAt: now,
      updatedBy: getUserId(),
      isDeleted: false,
      isBlocked: false,
    }));

    try {
      const response = await postMethod(
        "/api/Inventory/AddStocks",
        payload,
        "post"
      );
      if (response?.status === 200) {
        alert("Stocks added successfully.");
        clearDraftFromStorage();
        closeModal();
      } else {
        alert("Failed to save stocks.");
      }
    } catch (err) {
      console.error("Error adding stocks:", err);
      alert("An error occurred while saving the stocks.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = () => {
    saveDraftToStorage(stocks);
    alert("Draft saved locally.");
  };

  const handleClearDraft = () => {
    clearDraftFromStorage();
    alert("Draft cleared.");
  };

  return (
    <Card className="bg-secondary shadow">
      <CardHeader className="bg-white border-0">
        <Row className="align-items-center">
          <Col xs="8">
            <h3 className="mb-0">Add Stocks</h3>
          </Col>
          <Col className="text-right" xs="4">
            <Button close onClick={closeModal} />
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        <Form onSubmit={handleSubmit}>
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="thead-light">
                <tr>
                  <th>Product *</th>
                  <th>Quantity *</th>
                  <th>Source *</th>
                  <th>Reference Note </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {stocks.map((stock, index) => (
                  <tr key={index}>
                    <td>
                      <Input
                        type="select"
                        name="productId"
                        value={stock.productId}
                        onChange={(e) => handleChange(index, e)}
                        className="form-control-alternative"
                      >
                        <option value="">Select</option>
                        {products.map((prod) => (
                          <option key={prod.id} value={prod.id}>
                            {prod.productName}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="quantity"
                        value={stock.quantity}
                        onChange={(e) => handleChange(index, e)}
                        className="form-control-alternative"
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        name="source"
                        value={stock.source}
                        onChange={(e) => handleChange(index, e)}
                        className="form-control-alternative"
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        name="referenceNote"
                        value={stock.referenceNote}
                        onChange={(e) => handleChange(index, e)}
                        className="form-control-alternative"
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleRemove(index)}
                        disabled={stocks.length === 1}
                      >
                        <i className="fas fa-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {errors.some((e) => e) && (
            <div className="mb-3">
              {errors.map(
                (err, idx) =>
                  err && (
                    <div key={idx} style={{ color: "red" }}>
                      Row {idx + 1}: {err}
                    </div>
                  )
              )}
            </div>
          )}

          <div>
            <small>
              ⚠️ Note: Once stock is added, it cannot be modified or deleted.
              Please review and confirm your entries carefully before
              submission.
            </small>
          </div>

          <div className="d-flex justify-content-between mt-3">
            <Button color="primary" onClick={handleAdd} type="button">
              <i className="fas fa-plus" />
            </Button>
            <div>
              <Button
                color="warning"
                type="button"
                className="ml-2"
                onClick={handleSaveDraft}
              >
                Save Draft
              </Button>
              <Button
                color="danger"
                type="button"
                className="ml-2"
                onClick={handleClearDraft}
              >
                Clear Draft
              </Button>
              <Button color="success" type="submit" disabled={loading}>
                {loading ? "Saving..." : "Submit"}
              </Button>
            </div>
          </div>
        </Form>
      </CardBody>
    </Card>
  );
};

export default AddStocks;
